import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Phone, Calculator, ChevronDown, ChevronRight, CheckCircle2, ArrowRight } from "lucide-react";
import { SERVICE_PAGES } from "../lib/servicePages";
import { PAGE_UI, CLOSING_CTA } from "../lib/localPages";
import { CTABanner } from "../components/Shared";
import NotFoundPage from "./NotFound";
import { PHONE, PHONE_LINK, FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const Breadcrumb = ({ name, ui, section }) => (
  <nav aria-label="Breadcrumb" data-testid="service-breadcrumb" className="flex items-center flex-wrap gap-1.5 text-xs mb-6" style={{ fontFamily: FONT_BODY, color: "rgba(255,255,255,0.6)" }}>
    <Link to={ui === PAGE_UI.es ? "/es" : "/"} className="hover:text-white transition-colors">{ui.home}</Link>
    <ChevronRight size={12} />
    <Link to={section === "areas" ? ui.areasPath : ui.servicesPath} className="hover:text-white transition-colors">{section === "areas" ? ui.areas : ui.services}</Link>
    <ChevronRight size={12} />
    <span style={{ color: GOLD }}>{name}</span>
  </nav>
);

const MidCta = ({ ui }) => (
  <div data-testid="service-mid-cta" className="my-12 p-8 text-center" style={{ background: NAVY }}>
    <p className="text-lg font-bold text-white mb-4" style={{ fontFamily: FONT_HEAD }}>
      {ui.midCta}
    </p>
    <div className="flex flex-wrap justify-center gap-3">
      <a href={PHONE_LINK} className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all hover:opacity-90" style={{ background: GOLD, color: NAVY, fontFamily: FONT_BODY }}>
        <Phone size={16} /> {PHONE}
      </a>
      <Link to={ui.estimatePath} className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold border-2 text-white transition-all hover:bg-white hover:text-[#1B3A6B]" style={{ borderColor: "#FFFFFF", fontFamily: FONT_BODY }}>
        <Calculator size={16} /> {ui.midCtaBtn}
      </Link>
    </div>
  </div>
);

const ContentSection = ({ h2, paras, list, subsections, outro }) => (
  <div className="mb-10">
    <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{h2}</h2>
    {paras?.map((p) => (
      <p key={p.slice(0, 40)} className="text-base leading-relaxed mb-4" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>{p}</p>
    ))}
    {list && (
      <ul className="space-y-2.5 mt-2">
        {list.map((item) => (
          <li key={item.slice(0, 40)} className="flex items-start gap-2.5 text-base" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>
            <CheckCircle2 size={18} className="flex-shrink-0 mt-1" style={{ color: GOLD }} />
            {item}
          </li>
        ))}
      </ul>
    )}
    {outro?.map((p) => (
      <p key={p.slice(0, 40)} className="text-base leading-relaxed mt-5" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>{p}</p>
    ))}
    {subsections && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
        {subsections.map(({ h3, text }) => (
          <div key={h3} className="p-6" style={{ background: "#F9FAFB", borderLeft: `4px solid ${GOLD}` }}>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{h3}</h3>
            <p className="text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>{text}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ClosingCta = ({ lang, ui }) => {
  const c = CLOSING_CTA[lang];
  return (
    <p data-testid="service-closing-cta" className="text-base md:text-lg font-semibold leading-relaxed mt-2 mb-10 p-5" style={{ color: NAVY, fontFamily: FONT_BODY, background: "rgba(212,168,67,0.1)", borderLeft: `4px solid ${GOLD}` }}>
      {c.before}
      <a href={PHONE_LINK} className="underline underline-offset-4">{PHONE}</a>
      {c.mid}
      <Link to={ui.estimatePath} className="underline underline-offset-4">{c.link}</Link>
      {c.after}
    </p>
  );
};

const RecentWork = ({ photos, ui }) => (
  <div className="mb-10" data-testid="service-recent-work">
    <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{ui.recentWork}</h2>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {photos.map(({ src, alt }) => (
        <img key={src} src={src} alt={alt} loading="lazy" className="w-full h-44 md:h-52 object-cover shadow-sm" />
      ))}
    </div>
  </div>
);

const ServiceCta = ({ cta, testid, callLabel = "Call" }) => (
  <section data-testid={testid} className="py-16 md:py-20" style={{ background: NAVY }}>
    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: FONT_HEAD }}>{cta.h2}</h2>
      <p className="text-base mb-8 max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.7)", fontFamily: FONT_BODY }}>{cta.text}</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to={cta.primaryTo}
          data-testid={`${testid}-quote-btn`}
          className="flex items-center justify-center gap-2 px-8 py-4 text-base font-bold transition-all duration-300 hover:opacity-90"
          style={{ background: GOLD, color: NAVY, fontFamily: FONT_BODY }}
        >
          {cta.primaryLabel}
          <ArrowRight size={18} />
        </Link>
        <a
          href={PHONE_LINK}
          data-testid={`${testid}-call-btn`}
          className="flex items-center justify-center gap-2 px-8 py-4 text-base font-bold border-2 text-white transition-all duration-300 hover:bg-white hover:text-[#1B3A6B]"
          style={{ borderColor: "rgba(255,255,255,0.5)", fontFamily: FONT_BODY }}
        >
          <Phone size={18} />
          {callLabel} {PHONE}
        </a>
      </div>
    </div>
  </section>
);

const PageFaq = ({ faqs, ui }) => (
  <div className="mb-4" data-testid="service-faq">
    <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
      {ui.faqTitle}
    </h2>
    <div className="space-y-3">
      {faqs.map(({ q, a }, idx) => (
        <details key={q} data-testid={`service-faq-item-${idx}`} className="group bg-white" style={{ border: "1px solid rgba(27,58,107,0.1)" }}>
          <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-6 py-5 select-none">
            <h3 className="text-base font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{q}</h3>
            <ChevronDown size={18} className="flex-shrink-0 transition-transform duration-300 group-open:rotate-180" style={{ color: GOLD }} />
          </summary>
          <p className="px-6 pb-6 text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>{a}</p>
        </details>
      ))}
    </div>
  </div>
);

export function LocalPage({ pages, lang = "en", section = "services" }) {
  const { slug } = useParams();
  const page = pages.find((p) => p.slug === slug);
  const ui = PAGE_UI[lang];

  useEffect(() => {
    if (page) document.title = page.title;
  }, [page]);

  if (!page) return <NotFoundPage />;

  return (
    <div data-testid={`service-page-${page.slug}`} lang={lang}>
      <section className="pt-28 md:pt-36 pb-14" style={{ background: NAVY }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Breadcrumb name={page.breadcrumbName} ui={ui} section={section} />
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight" style={{ fontFamily: FONT_HEAD }} data-testid="service-page-h1">
            {page.h1}
          </h1>
        </div>
      </section>
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          {page.intro.map((p) => (
            <p key={p.slice(0, 40)} className="text-base md:text-lg leading-relaxed mb-5" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>{p}</p>
          ))}
          <img
            src={page.image}
            alt={page.imageAlt || `${page.keyword} — Cindyrella Cleaning LLC, licensed & insured in Naples, Florida`}
            loading="lazy"
            width="1200"
            height="800"
            className="w-full h-64 md:h-80 object-cover my-8 shadow-lg"
          />
          {page.sections.map((s) => (
            <ContentSection key={s.h2} {...s} />
          ))}
          {page.gallery?.length > 0 && <RecentWork photos={page.gallery} ui={ui} />}
          {page.closingCta && <ClosingCta lang={lang} ui={ui} />}
          <MidCta ui={ui} />
          {page.faqs?.length > 0 && <PageFaq faqs={page.faqs} ui={ui} />}
        </div>
      </section>
      <FinalCta page={page} lang={lang} ui={ui} />
    </div>
  );
}

const FinalCta = ({ page, lang, ui }) => {
  const testid = `service-page-cta-${page.slug}`;
  if (page.cta) return <ServiceCta cta={page.cta} testid={testid} />;
  if (lang === "en") return <CTABanner testid={testid} />;
  return <ServiceCta cta={{ h2: ui.ctaTitle, text: ui.ctaText, primaryLabel: ui.ctaBtn, primaryTo: ui.estimatePath }} testid={testid} callLabel={ui.callBtn} />;
};

export default function ServiceDetailPage() {
  return <LocalPage pages={SERVICE_PAGES} lang="en" section="services" />;
}
